
import React, { HTMLInputTypeAttribute, MouseEventHandler } from "react";
import { KIcon } from "@kyburz/kdesign-system";
import { InputLabel, TextField, TextFieldProps, Typography } from "@material-ui/core";
import clsx from "clsx";
import { useStyles } from "./styles";

interface Props {
  containerClassName?: string;
  inputClassName?: string;
  title?: string;
  name?: string;
  inputPlaceholder?: string;
  required?: boolean;
  disabled?: boolean;
  value: string | number | undefined;
  onChange?: TextFieldProps["onChange"];
  onKeyDown?: TextFieldProps["onKeyDown"];
  onBlur?: () => void;
  multiLine?: boolean;
  rowsMax?: number;
  rowsMin?: number;
  rows?: number;
  type: HTMLInputTypeAttribute;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  minInputValue?: number;
  maxInputValue?: number;
  autoComplete?: "off" | "on";
  icon?: {
    leftIcon?: string | JSX.Element;
    rightIcon?: string | JSX.Element;
    rightIconColor?: string;
  };
  // When input is a clickable box: // TODO: maybe separate in a different component? But reuse style by sharing css
  isClickable?: boolean; // changes the cursor type
  onStartIconClick?: () => void;
  onEndIconClick?: () => void;
  onClick?: MouseEventHandler<HTMLDivElement> | (() => void);
  autoFocus?: boolean;
  hasClearOption?: boolean;
  onClearInput?: (e: any) => void;
  backgroundColor?: string;
}

const TextInput = ({
  containerClassName,
  inputClassName,
  title,
  name,
  inputPlaceholder,
  required,
  disabled,
  value = "",
  onChange,
  onKeyDown,
  onBlur,
  autoComplete = "off",
  multiLine,
  rowsMax,
  rowsMin,
  rows,
  type,
  error,
  errorText,
  helperText,
  minInputValue,
  maxInputValue,
  icon,
  // When input is a clickable box:
  onStartIconClick = undefined,
  onEndIconClick = undefined,
  isClickable = false,
  onClick = () => {},
  autoFocus = false,
  hasClearOption = false,
  onClearInput = undefined,
  backgroundColor,
}: Props) => {
  const classes = useStyles({ isClickable, backgroundColor });

  // Determine if padding should be applied
  const additionalPadding: string =
    type === "textArea" ? classes.additionalPadding : "";
  const greyBackground: string = disabled ? classes.greyBackground : "";

  const inputProps:
    | { max?: number | undefined; min?: number | undefined; className?: string }
    | undefined = {
    ...(type === "number" && {
      ...{ min: minInputValue },
      ...{ max: maxInputValue },
    }),
    className: clsx(classes.inputInterior, inputClassName),
  };

  const leftIcon =
    typeof icon?.leftIcon === "string" ? (
      <KIcon
        icon={icon?.leftIcon}
        size={20}
        onClick={onStartIconClick || onClick}
      />
    ) : (
      icon?.leftIcon
    );

  const rightIcon =
    typeof icon?.rightIcon === "string" ? (
      <KIcon
        icon={icon.rightIcon}
        color={icon.rightIconColor}
        size={20}
        onClick={onEndIconClick || onClick}
      />
    ) : (
      icon?.rightIcon
    );

  return (
    <div className={`${containerClassName} ${classes.container}`}>
      {title && (
        <Typography variant="subtitle2" color="textPrimary">
          {title}
          {required && " *"}
        </Typography>
      )}
      <TextField
        className={clsx({
          [classes.outlinedWhiteBg]: true,
          [additionalPadding]: true,
          [greyBackground]: true,
          [classes.errorOutline]: error,
        })}
        id="outlined-basic"
        name={name}
        placeholder={inputPlaceholder}
        variant="outlined"
        size="small"
        value={value}
        autoComplete={autoComplete}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        multiline={multiLine}
        type={type}
        maxRows={rowsMax}
        rows={rows}
        minRows={rowsMin}
        InputProps={{
          inputProps,
          startAdornment: leftIcon,
          endAdornment: (hasClearOption || icon?.rightIcon) && (
            <div className={classes.iconsContainer} onClick={onClick}>
              {hasClearOption && (
                <KIcon
                  className={classes.clearOption}
                  icon="x"
                  size={16}
                  onClick={onClearInput}
                />
              )}
              {rightIcon}
            </div>
          ),
        }}
        onKeyDown={onKeyDown}
        // When input is a clickable box:
        onClick={onClick}
        autoFocus={autoFocus}
      />
      {errorText && error ? (
        <InputLabel classes={{ root: classes.error }}>{errorText}</InputLabel>
      ) : null}
      {helperText && !error ? (
        <InputLabel classes={{ root: classes.helper }}>{helperText}</InputLabel>
      ) : null}
    </div>
  );
};

export default TextInput;
